import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getPostBySlug, getRelatedPosts } from "@/data/blogPosts";

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#030305] text-white">
        <Header />
        <main className="pt-32 pb-24 px-6 max-w-2xl mx-auto text-center">
          <div className="font-mono-label text-[#D4FF4A]/70 mb-4">404</div>
          <h1 className="font-display text-3xl font-semibold mb-6">
            Post not found.
          </h1>
          <Link
            to="/blog"
            className="font-mono text-[#D4FF4A] text-sm hover:underline"
          >
            ← back to all posts
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const related = getRelatedPosts(slug, post.category);

  return (
    <div
      className="min-h-screen bg-[#030305] text-white"
      data-testid="blog-post-page"
    >
      <Header />

      <main className="pt-28 lg:pt-32 pb-24">
        <div className="max-w-[760px] mx-auto px-6 lg:px-8">
          <Link
            to="/blog"
            data-testid="blog-post-back-link"
            className="inline-flex items-center gap-1.5 font-mono text-[12px] text-[#71717A] hover:text-white mb-10"
          >
            <ArrowLeft size={13} />
            all posts
          </Link>

          <motion.article
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono-label text-[#D4FF4A]/80 text-[10px]">
                {post.category}
              </span>
              <span className="text-[#3F3F46]">·</span>
              <span className="font-mono text-[#71717A] text-[11px] flex items-center gap-1.5">
                <Clock size={12} />
                {post.readTime} min read
              </span>
              <span className="text-[#3F3F46]">·</span>
              <span className="font-mono text-[#71717A] text-[11px]">
                {post.publishedAt}
              </span>
            </div>

            <h1
              data-testid="blog-post-title"
              className="font-display text-white text-[36px] sm:text-[44px] lg:text-[56px] font-semibold leading-[1.05] tracking-[-0.03em] mb-8"
            >
              {post.title}
            </h1>

            <div className="flex items-center gap-3 pb-8 mb-10 border-b border-white/[0.06]">
              <div className="w-10 h-10 rounded-full bg-[#D4FF4A]/10 border border-[#D4FF4A]/30 flex items-center justify-center">
                <span className="font-display text-[#D4FF4A] text-[13px] font-semibold">
                  {post.author
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
                </span>
              </div>
              <div>
                <div className="text-white text-[14px] font-medium">
                  {post.author}
                </div>
                <div className="text-[#71717A] text-[12px]">
                  {post.authorRole}
                </div>
              </div>
            </div>

            <div data-testid="blog-post-content" className="prose-icpintel">
              {renderContent(post.content)}
            </div>

            {post.tags?.length > 0 && (
              <div className="mt-14 pt-8 border-t border-white/[0.06]">
                <div className="font-mono-label text-[#71717A] text-[10px] mb-3">
                  tags
                </div>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-[12px] text-[#A1A1AA] bg-white/[0.03] border border-white/[0.06] px-3 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </motion.article>

          {related.length > 0 && (
            <div className="mt-20 pt-10 border-t border-white/[0.06]">
              <div className="font-mono-label text-[#D4FF4A]/70 text-[10px] mb-6">
                Related reading
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {related.map((rp) => (
                  <Link
                    key={rp.slug}
                    to={`/blog/${rp.slug}`}
                    data-testid={`related-post-${rp.slug}`}
                    className="group block bg-[#0A0A0F] border border-white/[0.08] rounded-xl p-6 hover:border-white/[0.18] transition-colors"
                  >
                    <div className="font-mono-label text-[#D4FF4A]/70 text-[10px] mb-3">
                      {rp.category}
                    </div>
                    <h3 className="font-display text-white text-[18px] font-semibold leading-[1.25] mb-2 group-hover:text-[#D4FF4A] transition-colors">
                      {rp.title}
                    </h3>
                    <p className="text-[#A1A1AA] text-[13px] leading-[1.6] line-clamp-2">
                      {rp.excerpt}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-20 bg-[#0A0A0F] border border-white/[0.08] rounded-2xl p-8 lg:p-10 text-center">
            <div className="font-mono-label text-[#D4FF4A]/80 text-[10px] mb-3">
              Stop reading. Start finding.
            </div>
            <h3 className="font-display text-white text-[26px] sm:text-[32px] font-semibold mb-3 tracking-tight">
              Find your 10%.
            </h3>
            <p className="text-[#A1A1AA] text-[14px] mb-7 max-w-[440px] mx-auto">
              Upload a customer list. Get back the named buyers driving most of your
              revenue, plus the language they actually use.
            </p>
            <Link
              to="/#dashboard"
              data-testid="blog-post-cta"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4FF4A] text-[#030305] text-sm font-semibold rounded-full hover:bg-white transition-colors"
            >
              See the dashboard
              <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function renderContent(content) {
  if (!content) return null;
  const lines = content.split("\n");
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith("## ")) {
      elements.push(
        <h2
          key={i}
          className="font-display text-white text-[26px] lg:text-[30px] font-semibold leading-[1.2] tracking-[-0.02em] mt-14 mb-5"
        >
          {line.replace("## ", "")}
        </h2>
      );
    } else if (line.startsWith("- ")) {
      const listItems = [];
      while (i < lines.length && lines[i].startsWith("- ")) {
        const text = lines[i].replace("- ", "");
        listItems.push(
          <li
            key={i}
            className="flex items-start gap-3 text-[#D4D4D8] text-[17px] leading-[1.7]"
          >
            <span className="text-[#D4FF4A] mt-2 shrink-0 w-1.5 h-1.5 rounded-full bg-[#D4FF4A]" />
            <span>{renderInline(text)}</span>
          </li>
        );
        i++;
      }
      elements.push(
        <ul key={`list-${i}`} className="space-y-3 my-6">
          {listItems}
        </ul>
      );
      continue;
    } else if (line.trim() === "") {
      // skip
    } else {
      elements.push(
        <p
          key={i}
          className="text-[#D4D4D8] text-[17px] leading-[1.75] my-5"
        >
          {renderInline(line)}
        </p>
      );
    }
    i++;
  }

  return elements;
}

function renderInline(text) {
  const parts = text.split(/\*\*(.*?)\*\*/);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="text-white font-semibold">
        {part}
      </strong>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}
